import React, { useState } from 'react';
import {
  Text, HStack, VStack, Box, useMedia, Pressable, Heading,
} from '@gluestack-ui/themed';
import { useTranslation } from 'react-i18next';
import { ScrollView as RNScrollView, ViewStyle } from 'react-native';
import { VideoPlayer } from 'expo-video';
import { RecyclerListView, DataProvider, LayoutProvider } from 'recyclerlistview';
import {
  convertTranscriptJson, estimateCueRowHeight, findCueByTextAndIndex, formatTimestamp,
} from './transcriptHelpers';
import { useVideoPlaybackStatus, useVideoProgress } from '../store/videoPlayerStore';
import log from '../../../../../common/utils/logger';

export interface TranscriptData {
  cues: Array<{
    paragraph?: { start: number | string; text: string };
    headline?: { start: number | string; text: string };
  }>;
}

interface TranscriptCue {
  sequence: number;
  startTime: number;
  endTime: number;
  text: string;
  type: 'headline' | 'paragraph';
}

interface RowData extends TranscriptCue {
  isSelected: boolean;
}

interface InteractiveTranscriptProps {
  transcript: TranscriptData;
  width: number;
  videoRef: React.RefObject<VideoPlayer>;
}

const TranscriptItem: React.FC<{
  item: TranscriptCue;
  width: number;
  isSelected: boolean;
  onPress: () => void;
}> = ({
  item, width = 800, isSelected, onPress,
}) => {
  const media = useMedia();
  const isHeadline = item.type === 'headline';
  const isMobile = !media.md;

  // Adapt colors for mobile dark theme in video modal
  const textColor = isMobile ? '$textInverted' : '$textHeading';
  const timestampColor = isMobile ? '$textHint' : '$textBody';
  const selectedBg = isMobile ? '$backgroundDark800' : '$backgroundLight200';

  // Desktop layout
  if (media.md) {
    return (
      <Pressable onPress={!isHeadline ? onPress : undefined} width={width}>
        <HStack
          space="md"
          py="$2"
          px="$3"
          bg={isSelected && !isHeadline ? selectedBg : 'transparent'}
          alignItems="center"
          minHeight={48}
        >
          {!isHeadline && (
            <Text size="xs" color={timestampColor} w="$10" textAlign="left">
              {formatTimestamp(item.startTime)}
            </Text>
          )}
          <Text flex={1} fontWeight={isHeadline ? 'bold' : 'normal'} fontSize={isHeadline ? '$md' : '$sm'} color={textColor}>
            {item.text}
          </Text>
        </HStack>
      </Pressable>
    );
  }

  // Mobile layout - larger font for better readability
  return (
    <Pressable onPress={!isHeadline ? onPress : undefined}>
      <VStack py="$2" px="$3" bg={isSelected && !isHeadline ? selectedBg : 'transparent'} space="xs" minHeight={48}>
        {isHeadline ? (
          <Heading mt="$4" size="md" color={textColor}>
            {item.text}
          </Heading>
        ) : (
          <>
            <Text size="xs" color={timestampColor}>
              {formatTimestamp(item.startTime)}
            </Text>
            <Text size="sm" color={textColor}>{item.text}</Text>
          </>
        )}
      </VStack>
    </Pressable>
  );
};

const InteractiveTranscript: React.FC<InteractiveTranscriptProps> = ({ transcript, width, videoRef }) => {
  const { isFullscreen, isPlaying } = useVideoPlaybackStatus();
  const { position: videoPosition } = useVideoProgress();
  const media = useMedia();
  const isMobile = !media.md;

  const [layoutComplete, setLayoutComplete] = useState(false);

  const [selectedIndex, setSelectedIndex] = React.useState(0);
  // @ts-expect-error - RecyclerListView generics are difficult to type correctly with the current version
  const recyclerRef = React.useRef<RecyclerListView<TranscriptCue, RowData> | null>(null);
  const { t } = useTranslation();
  const [containerHeight, setContainerHeight] = React.useState(0);
  const [isScrolling, setIsScrolling] = React.useState(false);
  const scrollTimeoutRef = React.useRef<NodeJS.Timeout | null>(null);
  const lastSeekTimeRef = React.useRef<number>(0);
  const lastSeekIndexRef = React.useRef<number>(-1);
  const userClickedRef = React.useRef(false);

  // Prepare transcript data
  const cueArray = React.useMemo(() => (transcript ? convertTranscriptJson(transcript) : null), [transcript]);

  // Set up data provider with selection state
  const dataProvider = React.useMemo(() => new DataProvider((r1: RowData, r2: RowData) => (
    r1.sequence !== r2.sequence || r1.isSelected !== r2.isSelected
  )), []);

  // Update data provider with current selection
  const currentDataProvider = React.useMemo(() => {
    if (!cueArray) return dataProvider.cloneWithRows([]);
    const rowsWithSelection = cueArray.map((cue) => ({
      ...cue,
      isSelected: cue.sequence === selectedIndex,
    }));
    return dataProvider.cloneWithRows(rowsWithSelection);
  }, [cueArray, dataProvider, selectedIndex]);

  // Set up layout provider with dynamic heights
  const layoutProvider = React.useMemo(() => new LayoutProvider(
    (index) => cueArray?.[index]?.type || 'paragraph',
    (type, dim, index) => {
      // eslint-disable-next-line no-param-reassign
      dim.width = width;
      // RecyclerListView can request layout for an index while cueArray is
      // briefly shorter (size change / data swap). Never call `.text.length`
      // on a missing cue — that is FLARE-77.
      // eslint-disable-next-line no-param-reassign
      dim.height = estimateCueRowHeight(
        index !== undefined ? cueArray?.[index] : undefined,
        width,
      );
    },
  ), [width, cueArray]);

  // Handle seeking in transcript
  const handleSeek = React.useCallback(
    async (position: number, index: number) => {
      if (!videoRef.current) return;

      userClickedRef.current = true;
      setSelectedIndex(index);
      lastSeekTimeRef.current = position;
      lastSeekIndexRef.current = index;

      if (recyclerRef.current && layoutComplete) {
        recyclerRef.current.scrollToIndex(Math.max(0, index - 1), true);
      }

      try {
        // expo-video uses seconds, not milliseconds. Setting currentTime seeks the player.
        const player = videoRef.current;
        if (player) {
          player.currentTime = position / 1000;
        }
      } catch (error) {
        log.error('Error seeking video:', error);
      }
    },
    [videoRef, layoutComplete],
  );

  // Initialize first cue when layout is ready
  React.useEffect(() => {
    if (cueArray && layoutComplete && videoPosition !== undefined && videoPosition !== null && selectedIndex === 0) {
      const cueval = findCueByTextAndIndex(cueArray, videoPosition);
      if (cueval.cueindex !== -1 && cueval.cueindex !== selectedIndex) {
        setSelectedIndex(cueval.cueindex);
        lastSeekIndexRef.current = cueval.cueindex;
      }
    }
  }, [cueArray, layoutComplete, videoPosition, selectedIndex]);

  // Update selected paragraph based on video position
  React.useEffect(() => {
    if (!cueArray || videoPosition === undefined || videoPosition === null || isScrolling || isFullscreen || !layoutComplete) return;

    // Don't override user selection until video starts playing
    if (userClickedRef.current && !isPlaying) {
      return;
    }

    const cueval = findCueByTextAndIndex(cueArray, videoPosition);
    if (cueval.cueindex === -1) return;

    if (selectedIndex !== cueval.cueindex) {
      setSelectedIndex(cueval.cueindex);
      lastSeekIndexRef.current = cueval.cueindex;

      if (recyclerRef.current && !userClickedRef.current && layoutComplete) {
        recyclerRef.current.scrollToIndex(Math.max(0, cueval.cueindex - 1), true);
      }
    }
  }, [videoPosition, isPlaying, isFullscreen, cueArray, selectedIndex, isScrolling, layoutComplete]);

  // Reset user click flag when video starts playing
  React.useEffect(() => {
    if (isPlaying) {
      userClickedRef.current = false;
    }
  }, [isPlaying]);

  // Handle scroll events
  const handleScroll = React.useCallback(() => {
    setIsScrolling(true);
    if (scrollTimeoutRef.current) {
      clearTimeout(scrollTimeoutRef.current);
    }
    scrollTimeoutRef.current = setTimeout(() => {
      setIsScrolling(false);
    }, 150);
  }, []);

  // Cleanup scroll timeout
  React.useEffect(() => () => {
    if (scrollTimeoutRef.current) {
      clearTimeout(scrollTimeoutRef.current);
    }
  }, []);

  // Render row function for RecyclerListView
  const rowRenderer = React.useCallback(
    (type: string | number, data: RowData) => (
      <TranscriptItem
        item={data}
        width={width}
        isSelected={data.isSelected}
        onPress={() => handleSeek(data.startTime, data.sequence)}
      />
    ),
    [handleSeek, width],
  );

  // Hide the entire transcript UI while the video is in fullscreen mode. This preserves
  // hook order because the decision happens **after** every hook has been executed.
  if (isFullscreen) {
    return null;
  }

  // Mobile dark theme for transcript in video modal
  const textColor = isMobile ? '$textInverted' : '$textHeading';

  const isReadyToRender = cueArray
    && width >= 50
    && containerHeight >= 50
    && layoutComplete
    && !Number.isNaN(width)
    && !Number.isNaN(containerHeight)
    // Absolute defense: ensure floored dimensions are non-zero before passing to RecyclerListView
    && Math.floor(width) > 0
    && Math.floor(containerHeight) > 0;

  return (
    <VStack flex={1}>
      <Box
        flex={1}
        overflow="scroll"
        onLayout={(event) => {
          const { height } = event.nativeEvent.layout;
          // Only update if we have a valid height (prevents race condition)
          if (height > 0 && height !== containerHeight) {
            setContainerHeight(Math.floor(height));
            // Mark layout as complete when we get valid dimensions
            if (!layoutComplete) {
              setLayoutComplete(true);
            }
          }
        }}
      >
        {!isReadyToRender ? (
          <VStack flex={1} justifyContent="center" alignItems="center">
            {!cueArray || width < 100 ? (
              <Text alignSelf="center" color={textColor}>
                {t('student.task.videoplayer.messages.no-transcript')}
              </Text>
            ) : (
              <Box p="$4">
                {/* Optional: Add a subtle loading indicator here if needed */}
              </Box>
            )}
          </VStack>
        ) : (
          <RecyclerListView
            // @ts-expect-error - ref type mismatch with RecyclerListView
            ref={recyclerRef}
            layoutProvider={layoutProvider}
            dataProvider={currentDataProvider}
            rowRenderer={rowRenderer}
            onScroll={handleScroll}
            // @ts-expect-error - externalScrollView typing mismatch
            externalScrollView={RNScrollView}
            forceNonDeterministicRendering
            scrollViewProps={{
              showsVerticalScrollIndicator: true,
            }}
            // Use Math.floor for integer dimensions. Math.max(1, ...) ensures RecyclerListView
            // never receives a zero dimension during resize transitions — it throws a
            // LayoutException if height or width is zero, even when the isReadyToRender
            // guard would otherwise cause an unmount on the same render cycle.
            style={{
              height: Math.max(1, Math.floor(containerHeight)),
              width: Math.max(1, Math.floor(width)),
            } as ViewStyle}
            renderAheadOffset={Math.max(1, Math.floor(containerHeight * 2))}
            scrollThrottle={16}
            canChangeSize
            // Idempotent completion check
            onLayout={() => {
              if (!layoutComplete) setLayoutComplete(true);
            }}
          />
        )}
      </Box>
    </VStack>
  );
};

export default InteractiveTranscript;
